import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ArrowLeft, MapPin, Truck, Package, Clock, X, Store, ShoppingBag, Phone } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import React from 'react'; // ✅ FASE 4: Para React.memo optimization

import { NavbarPublic } from '../../components/NavbarPublic.jsx';
import { LazyImage } from '../../components/LazyImage.jsx'; // ✅ FASE 4: Lazy loading component
import { useDebounce } from '../../hooks/useDebounce.js'; // ✅ FASE 4: Debounce para búsqueda
import { prefetchRoute, prefetchImage } from '../../utils/prefetch.js'; // ✅ FASE 4: Prefetch utilities
import { api } from '@/lib/api.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../lib/dolar.ts';

// --- CONSTANTE DOMINIO R2 ---
const R2_DOMAIN = 'https://imagenes.tiendasya.store/';

// --- COMPONENTE DE FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-slate-50 dark:bg-[#050505] transition-colors duration-500">
    {/* ✅ OPTIMIZACIÓN FASE 3: Blur reducido de 120px → 60px para mejor GPU performance */}
    <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-indigo-300/30 dark:bg-indigo-900/20 rounded-full blur-[60px] opacity-40 dark:opacity-60 mix-blend-multiply dark:mix-blend-screen animate-pulse-slow" />
    {/* ✅ OPTIMIZACIÓN FASE 3: Blur reducido de 150px → 80px */}
    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-amber-200/40 dark:bg-amber-900/10 rounded-full blur-[80px] opacity-40 dark:opacity-50 mix-blend-multiply dark:mix-blend-screen" />
    {/* ✅ OPTIMIZACIÓN FASE 3: Blur reducido de 100px → 50px */}
    <div className="absolute top-[20%] left-[30%] w-[40vw] h-[40vw] bg-slate-300/30 dark:bg-slate-800/10 rounded-full blur-[50px]" />
  </div>
);

// --- COMPONENTE MODAL ---
function InfoModal({ isOpen, onClose, title, icon: Icon, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-lg bg-white dark:bg-[#0F0F0F] text-slate-900 dark:text-white rounded-[32px] shadow-2xl border border-slate-200 dark:border-white/10 animate-in zoom-in-95 duration-300">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-white/5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-white/80">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-light tracking-wider uppercase text-slate-900 dark:text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-white/50 transition-colors" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar text-slate-600 dark:text-white/70">
          {children}
        </div>
      </div>
    </div>
  );
}

// --- TARJETA DE PRODUCTO MEJORADA (Full Clickable) ---
// ✅ FASE 4: React.memo para evitar re-renders innecesarios
const ProductCard = React.memo(function ProductCard({ producto, slug, pricingRate }) {
  const originalPriceUSD = producto.price;
  const discountedPriceUSD = producto.discounted_price;
  const hasDiscount = discountedPriceUSD < originalPriceUSD;
  const activePromotion = producto.active_promotion;
  
  const displayPriceUSD = hasDiscount ? discountedPriceUSD : originalPriceUSD;
  const dualPrice = formatDualPrice(displayPriceUSD, pricingRate);
  
  const hasVariants = producto.variants && producto.variants.length > 0;
  const totalStock = hasVariants 
    ? producto.variants.reduce((sum, v) => sum + (v.stock || 0), 0)
    : producto.inventory;

  let promotionBadge = null;
  if (activePromotion) {
    if (activePromotion.promotion_type === 'percentage') {
      promotionBadge = `-${activePromotion.discount_value}%`;
    } else if (activePromotion.promotion_type === 'fixed') {
      promotionBadge = `-$${activePromotion.discount_value}`;
    } else if (activePromotion.promotion_type === 'buy_get') {
      promotionBadge = `${activePromotion.buy_quantity}x${activePromotion.free_quantity}`;
    }
  }

  // ✅ FASE 4: Prefetch inteligente en hover
  const handleMouseEnter = () => {
    // Prefetch de la página del producto
    prefetchRoute(`/t/${slug}/producto/${producto.id}`);
    
    // Prefetch de imágenes adicionales si las hay
    if (producto.variants && producto.variants.length > 0) {
      producto.variants.forEach(variant => {
        if (variant.image) {
          prefetchImage(variant.image);
        }
      });
    }
  };

  return (
    <Link 
      to={`/t/${slug}/producto/${producto.id}`}
      className="group relative flex flex-col bg-white dark:bg-[#0B0B0B] rounded-[32px] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/10 dark:hover:shadow-indigo-900/20 border border-slate-200 dark:border-white/5"
      onMouseEnter={handleMouseEnter} // ✅ FASE 4: Prefetch en hover
    >
      
      {/* Badge Promoción / Stock */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        {(promotionBadge || hasDiscount) && (
           <span className="px-3 py-1 rounded-full bg-[#111] border border-white/10 text-[10px] font-bold uppercase tracking-widest text-white shadow-md">
             {promotionBadge || `-${Math.round(((originalPriceUSD - discountedPriceUSD) / originalPriceUSD) * 100)}%`}
           </span>
        )}
        {totalStock === 0 && (
           <span className="px-3 py-1 rounded-full bg-red-500 text-[10px] font-bold uppercase tracking-widest text-white">Agotado</span>
        )}
      </div>

      {/* IMAGEN (Con Zoom) */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-white flex items-center justify-center p-6">
        {/* Efecto de luz de fondo */}
        <div className="absolute w-32 h-32 bg-slate-100/50 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        {/* ✅ FASE 4: Lazy loading optimizado para imágenes de productos */}
        <LazyImage
          src={producto.image || 'https://placehold.co/600x400/f8fafc/64748b?text=Sin+Imagen'}
          alt={producto.name}
          className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
          style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))' }}
          placeholder={
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <ShoppingBag className="h-12 w-12" />
            </div>
          }
          onError={() => {
            // Fallback si la imagen falla
            console.warn(`Failed to load product image: ${producto.image}`);
          }}
        />

        {/* Overlay oscuro sutil */}
        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      
      {/* INFO DEL PRODUCTO */}
      <div className="flex flex-col p-5 flex-1">
        <p className="text-[10px] font-bold tracking-[0.2em] text-slate-600 dark:text-white/60 uppercase mb-2 line-clamp-1">
           {producto.category || producto.description || 'Colección'}
        </p>
        
        <h2 className="text-lg font-medium text-slate-900 dark:text-white tracking-tight line-clamp-1 mb-4 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors">
          {producto.name}
        </h2>
        
        {/* PRECIOS AL FONDO */}
        <div className="mt-auto flex items-end justify-between border-t border-slate-100 dark:border-white/5 pt-4">
            <div className="flex flex-col">
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-light text-slate-900 dark:text-white">
                    {dualPrice.usd}
                  </span>
                  {hasDiscount && (
                    <span className="text-xs text-slate-500 line-through decoration-slate-500">
                       ${originalPriceUSD}
                    </span>
                  )}
                </div>
                {pricingRate && (
                   <span className="text-[10px] font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded w-fit mt-1">
                     {dualPrice.local}
                   </span>
                )}
            </div>

            {/* BOTÓN FANTASMA (Solo visual, indica acción) */}
            <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-white/10 flex items-center justify-center text-slate-400 dark:text-white/50 group-hover:bg-slate-900 dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-all duration-300 shadow-sm">
               <ShoppingBag className="h-4 w-4" />
            </div>
        </div>
      </div>
    </Link>
  );
}); // ✅ FASE 4: Cierre de React.memo

export function HomeTienda() {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const { data: pricingRate } = usePublicStorePricingRate(slug);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // ✅ PAGINACIÓN: Página actual
  const [selectedPromotion, setSelectedPromotion] = useState(null); // ✅ Promoción seleccionada para filtrar
  
  // ✅ FASE 4: Debounce para búsqueda - evita filtrar en cada keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Efecto para invalidar cache al montar el componente y asegurar datos frescos
  useEffect(() => {
    if (slug) {
      queryClient.invalidateQueries({ queryKey: ['tienda', slug] });
    }
  }, [slug, queryClient]);

  const { data: tienda } = useQuery({
    queryKey: ['tienda', slug],
    queryFn: async () => {
      // --- FIX CRÍTICO: CACHE BUSTING ---
      // Agregamos timestamp para evitar que el Service Worker o el Navegador
      // entreguen una versión vieja de la configuración de la tienda.
      const { data } = await api.get(`/api/stores/${slug}/public?_t=${Date.now()}`);
      return data;
    },
    enabled: Boolean(slug),
    // ✅ OPTIMIZACIÓN FASE 2: Cache inteligente para datos de tienda
    staleTime: 300000,        // 5 minutos - datos de tienda no cambian frecuentemente
    refetchInterval: 600000,  // 10 minutos - polling menos agresivo
    refetchOnWindowFocus: true // Recarga si cambias de pestaña y vuelves
  });
  
  // --- LÓGICA DE LOGO CORREGIDA ---
  let logoUrl = tienda?.logo;
  if (logoUrl && !logoUrl.startsWith('http')) {
      const cleanPath = logoUrl.startsWith('/') ? logoUrl.slice(1) : logoUrl;
      logoUrl = `${R2_DOMAIN}${cleanPath}`;
  }
  
  const settings = tienda?.settings || {};
  const branches = tienda?.branches || [];
  
  // ✅ OPTIMIZADO: Query con paginación y cache
  const { data: productosData, error: productosError } = useQuery({
    queryKey: ['tienda', slug, 'productos', currentPage],
    queryFn: async () => {
      try {
        const params = { page: currentPage };
        const { data } = await api.get(`/api/stores/${slug}/products`, { params });
        return data; // Devolver objeto completo con count, next, previous, results
      } catch (error) {
        console.error('Error loading store products:', error);
        // Si es 503 y tienda bloqueada, propagar el error
        if (error.response?.status === 503 && error.response?.data?.store_blocked) {
          throw error;
        }
        return { count: 0, results: [] };
      }
    },
    enabled: Boolean(slug),
    // ✅ OPTIMIZACIÓN: Cache de 60s en frontend (backend ya tiene cache de 60s)
    staleTime: 60000,           // Datos frescos por 1 minuto
    refetchInterval: false,     // No refetch automático (backend tiene cache)
    refetchOnWindowFocus: false, // No refetch al enfocar ventana
    refetchOnMount: false,      // No refetch al montar (usar cache)
    keepPreviousData: true,     // Mantener datos previos durante transiciones
  });

  // 🚫 VERIFICAR SI LA TIENDA ESTÁ BLOQUEADA
  if (productosError?.response?.status === 503 && productosError?.response?.data?.store_blocked) {
    return (
      <div className="min-h-screen text-slate-900 dark:text-white font-sans flex flex-col">
        <AmbientBackground />
        <NavbarPublic />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-20">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Icono */}
            <div className="mx-auto w-24 h-24 rounded-full bg-slate-100 dark:bg-white/5 border-2 border-slate-200 dark:border-white/10 flex items-center justify-center">
              <Store className="h-12 w-12 text-slate-400 dark:text-white/40" />
            </div>
            
            {/* Título */}
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              Tienda Temporalmente Cerrada
            </h1>
            
            {/* Descripción */}
            <p className="text-slate-600 dark:text-white/60 text-lg">
              Esta tienda está en mantenimiento. Por favor vuelve pronto.
            </p>
            
            {/* Botón volver */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 dark:bg-white text-white dark:text-black text-sm font-bold uppercase tracking-widest hover:bg-slate-700 dark:hover:bg-slate-200 transition-all shadow-lg"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al Inicio
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Extraer productos y metadata de paginación
  const productos = productosData?.results || [];
  const totalProductos = productosData?.count || 0;
  const totalPages = Math.ceil(totalProductos / 24);
  
  const { data: promociones } = useQuery({
    queryKey: ['tienda', slug, 'promociones'],
    queryFn: async () => {
        const { data } = await api.get(`/api/stores/${slug}/promotions`);
        return data;
    },
    enabled: Boolean(slug),
    // ✅ OPTIMIZACIÓN FASE 2: Cache moderado para promociones
    staleTime: 180000,        // 3 minutos - promociones pueden cambiar
    refetchInterval: 600000,  // 10 minutos - polling moderado
  });

  // ✅ FASE 4: Filtrado optimizado con debounce
  const filteredProductos = productos?.filter(producto => {
    // Filtro por búsqueda
    const matchesSearch = producto.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      producto.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    
    // Filtro por promoción seleccionada
    if (selectedPromotion) {
      const hasPromotion = producto.active_promotion?.id === selectedPromotion.id;
      return matchesSearch && hasPromotion;
    }
    
    return matchesSearch;
  }) || [];

  const dayLabels = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' };

  // Función para manejar clic en promoción
  const handlePromotionClick = (promo) => {
    setSelectedPromotion(promo);
    setCurrentPage(1); // Resetear a página 1
    setSearchTerm(''); // Limpiar búsqueda
    
    // Scroll al catálogo
    setTimeout(() => {
      const element = document.getElementById('catalogo-section');
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  // Función para limpiar filtro de promoción
  const clearPromotionFilter = () => {
    setSelectedPromotion(null);
  };

  return (
    <div className="min-h-screen text-slate-900 dark:text-white font-sans selection:bg-indigo-500/30 relative z-0 transition-colors duration-500">
      <AmbientBackground />
      <NavbarPublic />
      
      {/* MODALES */}
      <InfoModal isOpen={activeModal === 'branches'} onClose={() => setActiveModal(null)} title="Sucursales" icon={MapPin}>
        <div className="space-y-4">
          {branches.map(branch => (
            <div key={branch.id} className="p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
              <h4 className="font-bold text-slate-900 dark:text-white mb-1">{branch.name}</h4>
              <p className="text-sm text-slate-600 dark:text-white/60 mb-2">{branch.address}</p>
              {branch.phone && <p className="text-xs font-medium text-slate-500 dark:text-white/50">📞 {branch.phone}</p>}
              {branch.map_url && <a href={branch.map_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 uppercase tracking-widest">Ver Mapa →</a>}
            </div>
          ))}
        </div>
      </InfoModal>

      <InfoModal isOpen={activeModal === 'delivery'} onClose={() => setActiveModal(null)} title="Delivery" icon={Truck}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-white/60">{settings.delivery_info || 'Realizamos entregas a domicilio.'}</p>
          <div className="grid gap-2">
            {settings.delivery_rates?.map((rate, idx) => (
              <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
                <span className="font-medium text-slate-900 dark:text-white/90">{rate.name}</span>
                <span className={`px-2 py-1 rounded text-xs font-bold ${rate.price === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-white/70'}`}>
                  {rate.price === 0 ? 'GRATIS' : `$${rate.price}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </InfoModal>

      <InfoModal isOpen={activeModal === 'shipping'} onClose={() => setActiveModal(null)} title="Envíos Nacionales" icon={Package}>
         <p className="text-sm text-slate-600 dark:text-white/60 mb-4">{settings.shipping_info}</p>
         <div className="flex flex-wrap gap-2">{settings.shipping_agencies?.map(a => <span key={a} className="px-3 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-white/80 text-xs font-semibold border border-slate-200 dark:border-white/10 uppercase tracking-wider">{a}</span>)}</div>
      </InfoModal>

      <InfoModal isOpen={activeModal === 'pickup'} onClose={() => setActiveModal(null)} title="Horarios de Retiro" icon={Clock}>
          <p className="text-sm text-slate-600 dark:text-white/60 mb-4">{settings.pickup_info}</p>
          {Object.entries(settings.pickup_schedule || {}).map(([day, sch]) => sch.active && (
              <div key={day} className="flex justify-between p-2 border-b border-slate-200 dark:border-white/5 text-sm"><span className="capitalize text-slate-700 dark:text-white/80">{dayLabels[day]}</span><span className="text-slate-500 dark:text-white/50">{sch.start} - {sch.end}</span></div>
          ))}
      </InfoModal>

      <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 pt-32 pb-12">
        
        <div className="mb-8">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-slate-500 hover:text-slate-900 dark:text-white/50 dark:hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Volver al marketplace
          </Link>
        </div>

        {/* HEADER DE LUJO */}
        <header className="mb-16 text-center sm:text-left">
           <div className="flex flex-col sm:flex-row items-center justify-between gap-8">
              <div className="w-full sm:w-auto">
                 <p className="text-[10px] font-bold tracking-[0.3em] text-indigo-600 dark:text-indigo-400 uppercase mb-4 animate-in fade-in slide-in-from-bottom-2">
                    TIENDA OFICIAL
                 </p>

                 {/* LOGO MÓVIL - Visible solo en móvil */}
                 <div className="block sm:hidden mb-6 flex justify-center">
                    {/* Luz de fondo ambiental */}
                    <div className="relative">
                       <div className="absolute inset-0 bg-indigo-500/20 blur-[40px] rounded-full opacity-50 dark:opacity-80" />
                       
                       {logoUrl ? (
                           <div className="relative w-24 h-24 rounded-full p-1 bg-gradient-to-br from-white/10 to-transparent border border-white/10 backdrop-blur-sm shadow-xl">
                               <img 
                                 src={logoUrl} 
                                 alt="Logo de la tienda" 
                                 className="w-full h-full object-cover rounded-full bg-white dark:bg-black" 
                               />
                           </div>
                       ) : (
                           <div className="relative w-24 h-24 flex items-center justify-center border border-slate-200 dark:border-white/10 rounded-full bg-white/50 dark:bg-white/5 backdrop-blur-sm shadow-xl">
                              <Store className="w-10 h-10 text-slate-400 dark:text-white/20" />
                           </div>
                       )}
                    </div>
                 </div>
                 
                 <h1 className="text-4xl sm:text-6xl font-extralight tracking-tight text-slate-900 dark:text-white mb-4 uppercase">
                    {tienda?.name ?? 'Cargando...'}
                 </h1>

                 {/* CONTACTO MÓVIL - Visible solo en móvil, justo después del nombre */}
                 <div className="block sm:hidden mb-6">
                    <div className="flex flex-col items-center gap-3">
                       {tienda?.phone && (
                         <a 
                           href={`tel:${tienda.phone}`}
                           className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-all text-xs font-bold uppercase tracking-widest"
                         >
                           <Phone className="h-3 w-3" />
                           {tienda.phone}
                         </a>
                       )}
                       
                       {settings?.whatsapp_enabled && settings?.whatsapp_number && (
                         <a 
                           href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Hola, me interesa conocer más sobre sus productos`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-all text-xs font-bold uppercase tracking-widest"
                         >
                           <div className="w-3 h-3 rounded-full bg-green-500 flex items-center justify-center">
                             <span className="text-white text-[8px] font-bold">W</span>
                           </div>
                           WhatsApp
                         </a>
                       )}
                    </div>
                 </div>
                 
                 <p className="text-slate-600 dark:text-white/60 max-w-md text-sm sm:text-base font-light leading-relaxed mx-auto sm:mx-0">
                    {tienda?.description}
                 </p>

                 <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-8">
                    {branches.length > 0 && (
                       <button onClick={() => setActiveModal('branches')} className="group flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 transition-all">
                          <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 group-hover:shadow-[0_0_10px_#6366f1]" />
                          <span className="text-[10px] font-bold tracking-widest uppercase text-slate-700 dark:text-white/80">Sucursales</span>
                       </button>
                    )}
                    {settings.delivery_enabled && (
                       <button onClick={() => setActiveModal('delivery')} className="group flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 transition-all">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:shadow-[0_0_10px_#10b981]" />
                          <span className="text-[10px] font-bold tracking-widest uppercase text-slate-700 dark:text-white/80">Delivery</span>
                       </button>
                    )}
                    {settings.shipping_enabled && (
                       <button onClick={() => setActiveModal('shipping')} className="group flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 transition-all">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover:shadow-[0_0_10px_#f59e0b]" />
                          <span className="text-[10px] font-bold tracking-widest uppercase text-slate-700 dark:text-white/80">Envíos</span>
                       </button>
                    )}
                     {settings.pickup_enabled && (
                       <button onClick={() => setActiveModal('pickup')} className="group flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 transition-all">
                          <div className="w-1.5 h-1.5 rounded-full bg-pink-500 group-hover:shadow-[0_0_10px_#ec4899]" />
                          <span className="text-[10px] font-bold tracking-widest uppercase text-slate-700 dark:text-white/80">Horarios</span>
                       </button>
                    )}
                 </div>
              </div>

              {/* LOGO DESKTOP - Visible solo en desktop */}
              <div className="hidden sm:block relative">
                  {/* Luz de fondo ambiental */}
                  <div className="absolute inset-0 bg-indigo-500/20 blur-[60px] rounded-full opacity-50 dark:opacity-80" />
                  
                  <div className="flex flex-col items-center gap-6">
                     {logoUrl ? (
                         <div className="relative w-40 h-40 rounded-full p-1.5 bg-gradient-to-br from-white/10 to-transparent border border-white/10 backdrop-blur-sm shadow-2xl">
                             <img 
                               src={logoUrl} 
                               alt="Logo de la tienda" 
                               className="w-full h-full object-cover rounded-full bg-white dark:bg-black" 
                             />
                         </div>
                     ) : (
                         <div className="relative w-40 h-40 flex items-center justify-center border border-slate-200 dark:border-white/10 rounded-full bg-white/50 dark:bg-white/5 backdrop-blur-sm shadow-xl">
                            <Store className="w-16 h-16 text-slate-400 dark:text-white/20" />
                         </div>
                     )}

                     {/* CONTACTO DESKTOP - Debajo del logo */}
                     <div className="flex flex-col items-center gap-3">
                        {tienda?.phone && (
                          <a 
                            href={`tel:${tienda.phone}`}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-all text-xs font-bold uppercase tracking-widest"
                          >
                            <Phone className="h-3 w-3" />
                            {tienda.phone}
                          </a>
                        )}
                        
                        {settings?.whatsapp_enabled && settings?.whatsapp_number && (
                          <a 
                            href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Hola, me interesa conocer más sobre sus productos`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-all text-xs font-bold uppercase tracking-widest"
                          >
                            <div className="w-3 h-3 rounded-full bg-green-500 flex items-center justify-center">
                              <span className="text-white text-[8px] font-bold">W</span>
                            </div>
                            WhatsApp
                          </a>
                        )}

                        {tienda?.website && (
                          <a 
                            href={tienda.website} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 transition-all text-xs font-bold uppercase tracking-widest"
                          >
                            Website
                          </a>
                        )}
                     </div>
                  </div>
              </div>
           </div>
        </header>

        {/* BARRA BUSCADOR */}
        <div className="sticky top-20 z-30 mb-12">
            <div className="mx-auto max-w-3xl">
                <div className="relative group">
                    <div className="absolute inset-0 bg-white/50 dark:bg-white/5 blur-xl rounded-full group-focus-within:bg-indigo-500/20 transition-all duration-300" />
                    <div className="relative flex items-center bg-white/80 dark:bg-[#0F0F0F]/80 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-full px-6 py-3 shadow-xl dark:shadow-2xl">
                        <Search className="w-5 h-5 text-slate-400 dark:text-white/40 group-focus-within:text-slate-900 dark:group-focus-within:text-white transition-colors" />
                        <input 
                            type="text"
                            placeholder="BUSCAR EN EL CATÁLOGO..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-transparent border-none focus:ring-0 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-white/30 text-sm tracking-wider ml-4 h-full py-1 uppercase focus:outline-none"
                        />
                    </div>
                </div>
            </div>
        </div>

        {/* PROMOCIONES */}
        {promociones && promociones.length > 0 && (
          <section className="mb-16">
            <div className="flex items-end justify-between border-b border-slate-200 dark:border-white/5 pb-4 mb-8">
                <h2 className="text-2xl font-light uppercase tracking-[0.2em] text-slate-900 dark:text-white">Ofertas</h2>
                <span className="text-xs font-mono text-slate-500 dark:text-white/40">LIMITED TIME</span>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 py-4">
              {promociones.slice(0, 6).map((promo) => (
                <button
                  key={promo.id}
                  onClick={() => handlePromotionClick(promo)}
                  className="group relative overflow-visible rounded-[32px] bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-[1px] hover:shadow-2xl hover:shadow-indigo-500/25 transition-all duration-500 hover:-translate-y-1 cursor-pointer w-full text-left"
                >
                  {/* Contenedor interno con layout dividido estilo tarjeta de negocio */}
                  <div className="relative overflow-hidden rounded-[31px] bg-[#0B0B0B] min-h-[140px] flex">
                    {/* Efectos de fondo */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-purple-900/10 to-pink-900/20 opacity-60"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/10 to-transparent rounded-bl-full -mr-8 -mt-8 group-hover:scale-125 transition-transform duration-700"></div>
                    
                    {/* Imagen del producto integrada profesionalmente */}
                    {promo.products && promo.products[0]?.image && (
                      <div className="absolute top-1/2 -translate-y-1/2 right-4 z-20 transform group-hover:-translate-y-[calc(50%-8px)] group-hover:scale-110 transition-all duration-500">
                        <div className="w-28 h-28 rounded-2xl overflow-hidden shadow-lg bg-white/10 backdrop-blur-sm border border-white/20">
                          <LazyImage
                            src={promo.products[0].image}
                            alt={promo.products[0].name}
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                          />
                        </div>
                      </div>
                    )}
                    <div className="relative z-10 p-5 pr-36 flex flex-col justify-between w-full">
                      <div>
                        <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-indigo-300 transition-colors line-clamp-1">{promo.title}</h3>
                        <p className="text-white/70 text-xs mb-3 line-clamp-2">{promo.description}</p>
                      </div>
                      <div className="inline-block px-3 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold uppercase tracking-wider shadow-lg transform hover:scale-105 transition-transform w-fit">
                      {promo.promotion_type === 'percentage' ? `${promo.discount_value}% OFF` : `$${promo.discount_value} OFF`}
                    </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* CATÁLOGO */}
        <section id="catalogo-section">
          <div className="mb-8 flex items-end justify-between border-b border-slate-200 dark:border-white/5 pb-4">
            <h2 className="text-2xl font-light uppercase tracking-[0.2em] text-slate-900 dark:text-white">Colección</h2>
            <span className="text-xs font-mono text-slate-500 dark:text-white/40">{filteredProductos.length} Items</span>
          </div>

          {/* Banner de filtro activo */}
          {selectedPromotion && (
            <div className="mb-6 flex items-center justify-between p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Mostrando productos de: <span className="font-bold">{selectedPromotion.title}</span>
                  </p>
                  <p className="text-xs text-slate-600 dark:text-white/60">
                    {selectedPromotion.promotion_type === 'percentage' ? `${selectedPromotion.discount_value}% de descuento` : `${selectedPromotion.discount_value} de descuento`}
                  </p>
                </div>
              </div>
              <button
                onClick={clearPromotionFilter}
                className="px-4 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-black text-xs font-bold uppercase tracking-wider hover:bg-slate-700 dark:hover:bg-slate-200 transition-all"
              >
                Ver todos
              </button>
            </div>
          )}

          <div className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(filteredProductos.length > 0 ? filteredProductos : (productos ?? [])).map((producto) => (
              <ProductCard key={producto.id} producto={producto} slug={slug} pricingRate={pricingRate} />
            ))}
          </div>

          {filteredProductos.length === 0 && debouncedSearchTerm && (
            <div className="py-32 text-center opacity-50">
                <p className="text-xl font-light uppercase tracking-widest mb-2">Sin resultados</p>
                <p className="text-sm font-mono">Intenta con otra búsqueda</p>
            </div>
          )}

          {/* ✅ PAGINACIÓN PROFESIONAL - Solo si NO hay filtro de promoción */}
          {totalPages > 1 && !debouncedSearchTerm && !selectedPromotion && (
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-slate-200 dark:border-white/5 pt-8">
              {/* Info de página */}
              <div className="text-sm text-slate-600 dark:text-white/50">
                Mostrando <span className="text-slate-900 dark:text-white font-medium">{((currentPage - 1) * 24) + 1}</span> - <span className="text-slate-900 dark:text-white font-medium">{Math.min(currentPage * 24, totalProductos)}</span> de <span className="text-slate-900 dark:text-white font-medium">{totalProductos}</span> productos
              </div>

              {/* Botones de paginación */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setCurrentPage(p => Math.max(1, p - 1));
                    setTimeout(() => {
                      const element = document.getElementById('catalogo-section');
                      if (element) {
                        const yOffset = -100;
                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }, 0);
                  }}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm font-medium hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Anterior
                </button>

                {/* Números de página */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => {
                          setCurrentPage(pageNum);
                          setTimeout(() => {
                            const element = document.getElementById('catalogo-section');
                            if (element) {
                              const yOffset = -100;
                              const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }, 0);
                        }}
                        className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-slate-900 dark:bg-white text-white dark:text-black'
                            : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-white/70 hover:bg-slate-200 dark:hover:bg-white/10'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => {
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                    setTimeout(() => {
                      const element = document.getElementById('catalogo-section');
                      if (element) {
                        const yOffset = -100;
                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }, 0);
                  }}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm font-medium hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Footer con Términos y Condiciones */}
        <footer className="mt-16 border-t border-white/5 pt-8 pb-6">
          <div className="text-center space-y-3">
            <p className="text-xs text-white/40">
              © 2026 {tienda?.nombre || 'Tienda'}. Todos los derechos reservados.
            </p>
            <div className="flex items-center justify-center gap-4 text-xs">
              <a
                href="/terminos-clientes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-emerald-400 transition-colors underline"
              >
                Términos y Condiciones
              </a>
              <span className="text-white/20">•</span>
              <span className="text-white/30">
                Powered by <span className="text-emerald-400 font-semibold">TiendasYA</span>
              </span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}